import type { Area } from "@/lib/types";
import type { NivelAutorizacion } from "../services/niveles-service";

// ---------------------------------------------------------------------------
// Vista matriz · agrupa por (tipo, subtipo) y dentro por área, para pintar una
// tabla área × nivel (mucho más compacta que un card por combinación).
// ---------------------------------------------------------------------------

export interface FilaAreaNiveles {
  areaId: string;
  areaNombre: string;
  /** Nivel por número (1..N) · null si esa área no tiene ese nivel configurado. */
  porNumero: Map<number, NivelAutorizacion>;
}

export interface GrupoTipoSubtipo {
  key: string;
  tipo: string;
  subtipo: string;
  /** Nivel máximo presente en el grupo · define las columnas de la matriz. */
  maxNivel: number;
  areas: FilaAreaNiveles[];
}

/**
 * Agrupa por (tipo, subtipo) y, dentro, por área · función pura. Cada grupo
 * alimenta una matriz donde las filas son áreas y las columnas los niveles
 * (1..maxNivel). Ordena áreas alfabéticamente y grupos por tipo/subtipo.
 */
export function agruparPorTipoSubtipo(
  niveles: NivelAutorizacion[],
  areas: Area[]
): GrupoTipoSubtipo[] {
  const areaPorId = new Map<string, string>();
  for (const a of areas) areaPorId.set(a.id, a.nombre);

  const map = new Map<string, GrupoTipoSubtipo>();
  const filasPorGrupoArea = new Map<string, Map<string, FilaAreaNiveles>>();

  for (const n of niveles) {
    const gKey = `${n.tipo}__${n.subtipo}`;
    let grupo = map.get(gKey);
    if (!grupo) {
      grupo = { key: gKey, tipo: n.tipo, subtipo: n.subtipo, maxNivel: 0, areas: [] };
      map.set(gKey, grupo);
      filasPorGrupoArea.set(gKey, new Map());
    }
    grupo.maxNivel = Math.max(grupo.maxNivel, n.nivel);

    const filas = filasPorGrupoArea.get(gKey)!;
    let fila = filas.get(n.areaId);
    if (!fila) {
      fila = {
        areaId: n.areaId,
        areaNombre: areaPorId.get(n.areaId) ?? n.areaId.slice(0, 8) + "…",
        porNumero: new Map(),
      };
      filas.set(n.areaId, fila);
      grupo.areas.push(fila);
    }
    fila.porNumero.set(n.nivel, n);
  }

  const grupos = Array.from(map.values());
  for (const grupo of grupos) {
    grupo.areas.sort((a, b) => a.areaNombre.localeCompare(b.areaNombre));
  }
  grupos.sort(
    (a, b) => a.tipo.localeCompare(b.tipo) || a.subtipo.localeCompare(b.subtipo)
  );
  return grupos;
}
